import React, { useEffect, useRef, useState } from 'react';
import './styles.css';

import Card from 'react-bootstrap/Card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeart,
  faComment,
  faPaperPlane,
  faBookmark
} from '@fortawesome/free-solid-svg-icons';
import Image from 'react-bootstrap/Image';
import Carousel from 'react-bootstrap/Carousel';
import VideoComponent from './VideoComponent';
import { useTranslate } from '@tolgee/react';
import { PREVIEW_TYPE_COMPACT } from '../../config/constants';
import { useDispatch, useSelector } from 'react-redux';
import { COMPACT_MODE_HEIGHT } from '../contants';
import PreviewImagesSkeleton from '../loaders/previewImagesSkeleton';
import OnImagesLoaded from '../../../onImagesLoaded';
import {
  CROP_RATIOS,
  IG_POST_MAX_RATIO,
  IG_POST_MIN_RATIO,
  platformFeatures,
  POST_TYPE_KEY,
  POST_TYPE_POST_KEY
} from '../../config/platformFeatures';
import { INSTAGRAM } from '../../../../config/constants';
import { set } from 'lodash';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import ImportedPostsMediaPreview from './importedPostsMediaPreview';
import { setModalUiContent } from '@/redux/slices/lomavisCreator/lomavisCreator';
import { lomavisCreatorRootState } from '@/pages/lomavisCreator';
import AltTagButton from './buttons/altTagsBtn';
import { useInstagramMusicControls } from '../modals/InstagramMusic/useInstagramMusicControls';
import { LomavisOverlayToolTip1 } from '@/lomavisSharedUi/atoms/overlays/lomavisOverlayToolTip1/lomavisOverlayToolTip1';

const isVideoMedia = (media) =>
  media?.type === 'video' || media?.media_type === 'video';

const getVideoUrl = (media) =>
  media?.file ||
  media?.video_url ||
  media?.original_asset ||
  media?.assetBlob ||
  media?.preview_image;

export default function InstagramPostPreview({
  platform = INSTAGRAM,
  mediaFiles = [],
  postText = '',
  profileName = '',
  platformIcon = '',
  previewAssets,
  viewAssets
}) {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const [index, setIndex] = useState(0);
  const [aspectRatio, setAspectRatio] = useState(null);
  const [knownAspectRatio, setKnownAspectRatio] = useState(true);
  const {
    general_post_type,
    created_in_lomavis,
    instagram_direct_connect,
    ui: { previewMode }
  } = useSelector((state: any) => state.lomavisCreator);

  const instagramProductTags = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[INSTAGRAM]?.additional_publication_data
        ?.instagram_shopping_product_tags || {}
  );
  const {
    music: instagramMusic,
    openMusicModal,
    hasVideo,
    musicUnavailableReason
  } = useInstagramMusicControls(INSTAGRAM);
  const ratioIsInAcceptedRange = (firstMediaFile) => {
    /*
    Function to check if the image dimensions are accepted by Instagram. Instagram accepts images with aspect ratios
    between 4:5 and 1.91:1. */
    const ratio = firstMediaFile.width / firstMediaFile.height;

    return ratio <= IG_POST_MAX_RATIO && ratio >= IG_POST_MIN_RATIO;
  };

  useEffect(() => {
    const instagramConfig = platformFeatures[INSTAGRAM][POST_TYPE_KEY].find(
      (config) => config.typeKey === POST_TYPE_POST_KEY
    );

    const cropsWithoutPadding =
      instagramConfig?.mediaTypes?.image?.cropsWithoutPadding;
    const defaultCropRatio =
      instagramConfig?.mediaTypes?.image?.defaultCropRatio;

    // Get the first media file
    const firstMediaFile = mediaFiles[0];

    if (!firstMediaFile) {
      // Reset aspect ratio if there is no media file
      setAspectRatio(null);
      return;
    }

    const ratio = firstMediaFile.width / firstMediaFile.height;
    let isKnownAspectRatio = false;

    if (cropsWithoutPadding && cropsWithoutPadding.length > 0) {
      isKnownAspectRatio = cropsWithoutPadding.some((key) => {
        const crop = CROP_RATIOS[key];
        return crop.width / crop.height === ratio;
      });
    }

    const isAcceptedRatio = ratioIsInAcceptedRange(firstMediaFile);

    // if (isKnownAspectRatio) {
    //   setAspectRatio(ratio);
    //   setKnownAspectRatio(true);
    // } else {
    //   setKnownAspectRatio(false);
    //   if (defaultCropRatio) {
    //     // We were previously setting a default ratio if the current ratio is not part of our cropsWithoutPadding options
    //     setAspectRatio(
    //       CROP_RATIOS[defaultCropRatio].width /
    //         CROP_RATIOS[defaultCropRatio].height
    //     );

    //   } else {
    //     setAspectRatio(null);
    //   }
    // }

    if (isAcceptedRatio) {
      setAspectRatio(ratio);
      setKnownAspectRatio(true);
    } else {
      setKnownAspectRatio(false);
      if (defaultCropRatio) {
        setAspectRatio(
          CROP_RATIOS[defaultCropRatio].width /
            CROP_RATIOS[defaultCropRatio].height
        );
      } else {
        setAspectRatio(null);
      }
    }
  }, [mediaFiles, general_post_type]);

  const videoRefs = useRef([]);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  // Initialize videoRefs when mediaFiles change
  useEffect(() => {
    videoRefs.current = mediaFiles.map(() => React.createRef());
  }, [mediaFiles]);

  const handleSelect = (selectedIndex) => {
    // Pause the current video if it's a video
    const currentMedia = mediaFiles[index];
    if (isVideoMedia(currentMedia)) {
      const currentVideo = videoRefs.current[index]?.current;
      if (currentVideo) currentVideo.pause();
    }

    setIndex(selectedIndex);

    // Play the next video if it's a video
    const nextMedia = mediaFiles[selectedIndex];
    if (isVideoMedia(nextMedia)) {
      const nextVideo = videoRefs.current[selectedIndex]?.current;
      // if (nextVideo) nextVideo.play();
    }
  };

  useEffect(() => {
    // Play the initial video if it's a video
    const initialMedia = mediaFiles[index];
    if (isVideoMedia(initialMedia)) {
      const initialVideo = videoRefs.current[index]?.current;
      if (initialVideo) initialVideo.play();
    }
  }, []); // Run once on mount

  // Helper constants
  const hasMediaFiles = mediaFiles.length > 0;
  const isSingleMedia = mediaFiles.length === 1;
  const previewHasVideo = mediaFiles.some(isVideoMedia);
  const previewHasSingleVideo = isSingleMedia && isVideoMedia(mediaFiles[0]);
  const previewMusicDisabled = previewHasVideo && !previewHasSingleVideo;
  const showInstagramMusicControls =
    !instagram_direct_connect && (hasVideo || previewHasVideo);
  const instagramPreviewAudio =
    showInstagramMusicControls &&
    previewHasSingleVideo &&
    instagramMusic?.download_url
      ? {
          audioUrl: instagramMusic.download_url,
          audioVolume: instagramMusic.audio_volume ?? 100,
          videoVolume: instagramMusic.video_volume ?? 100
        }
      : {};
  // Edit Button Component
  const EditButton = ({ uuid }) => (
    <div
      style={{ zIndex: 3 }}
      className="d-flex gap-2 justify-content-center align-items-center position-absolute ms-auto me-3 mt-3 end-0"
    >
      <div
        className={`viewButton btn btn-sm btn-bg-white border border-dark px-3 position-relative ${
          instagramProductTags &&
          instagramProductTags[uuid]?.length > 0 &&
          'me-1'
        }`}
        onClick={() => {
          dispatch(
            setModalUiContent({
              modal: 'IGProductTags',
              attribute: 'toggle',
              value: true
            })
          );
        }}
      >
        <i
          style={{ padding: '0px' }}
          className=" fa-solid fa-bag-shopping text-dark"
        ></i>

        {instagramProductTags && instagramProductTags[uuid]?.length > 0 && (
          <div
            className="w-20px h-20px position-absolute top-100 start-100 translate-middle rounded bg-warning d-flex justify-content-center align-items-center"
            style={{
              alignItems: 'center',
              zIndex: 10
            }}
          >
            <span className="text-white" role="status" aria-hidden="true">
              {instagramProductTags && instagramProductTags[uuid]?.length}
            </span>
          </div>
        )}
      </div>

      {showInstagramMusicControls &&
        (() => {
          const musicButtonLabel = instagramMusic?.audio_id
            ? t('lomavisCreator.IG.music.editMusic')
            : t('lomavisCreator.IG.music.addMusic');
          const musicButton = (
            <div
              className={`viewButton btn btn-sm btn-bg-white border px-3 ${
                previewMusicDisabled
                  ? 'border-secondary opacity-75'
                  : 'border-dark'
              }`}
              onClick={previewMusicDisabled ? undefined : openMusicModal}
              title={
                previewMusicDisabled
                  ? musicUnavailableReason
                  : musicButtonLabel
              }
              aria-label={
                previewMusicDisabled
                  ? musicUnavailableReason
                  : musicButtonLabel
              }
              aria-disabled={previewMusicDisabled}
              style={{
                cursor: previewMusicDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              <i
                style={{ padding: '0px' }}
                className={`fa-solid fa-music ${
                  previewMusicDisabled ? 'text-muted' : 'text-dark'
                }`}
              ></i>
            </div>
          );

          return previewMusicDisabled ? (
            <LomavisOverlayToolTip1
              text={musicUnavailableReason}
              placement="top"
            >
              {musicButton}
            </LomavisOverlayToolTip1>
          ) : (
            musicButton
          );
        })()}

      <div
        className="viewButton btn btn-sm btn-bg-white border border-dark px-3"
        onClick={() => viewAssets()}
      >
        <i
          style={{ padding: '0px' }}
          className="fa-regular fa-eye text-dark"
        ></i>
      </div>

      <AltTagButton platform={platform} />

      <div
        className="btn btn-sm btn-bg-white border border-dark cropEditBtn firstImageCropEditBtn"
        onClick={() => previewAssets(index)}
      >
        <span className="loading-indicator spinner-border spinner-border-sm d-none"></span>
        <span>{t('lomavisCreator.preview.edit')}</span>
      </div>
    </div>
  );

  // Render Media Content
  const renderMediaContent = () => {
    const commonStyles = {
      maxHeight:
        previewMode === PREVIEW_TYPE_COMPACT ? COMPACT_MODE_HEIGHT : '100%'
    };

    if (isSingleMedia) {
      const media = mediaFiles[0];
      return (
        <div className="position-relative">
          {isImageLoaded && <EditButton uuid={media?.uuid} />}
          {isVideoMedia(media) ? (
            <div className="w-100 h-100 d-flex justify-content-center align-items-center">
              <VideoComponent
                posterUrl={media.custom_thumbnail_url}
                backgroundUrl={media.thumbnail_url}
                videoUrl={getVideoUrl(media)}
                styles={{
                  ...commonStyles,
                  height: '100%'
                }}
                autoplay={false}
                ref={videoRefs.current[0]}
                useProcessedFrame={true}
                {...instagramPreviewAudio}
              />
            </div>
          ) : (
            <div
              className="w-100 h-100"
              style={{
                aspectRatio,
                background: `url(${media.image_url}) no-repeat center center`
              }}
            >
              <Image
                className="img-fluid w-100 h-100"
                style={{
                  objectFit: 'contain',
                  backdropFilter: 'blur(5px)',
                  ...commonStyles
                }}
                src={media.image_url}
              />
            </div>
          )}
        </div>
      );
    } else {
      return (
        <Carousel
          interval={null}
          activeIndex={index}
          onSelect={handleSelect}
          onTouchStart={(e) => {
            e.preventDefault();
          }}
        >
          {mediaFiles.map((media, idx) => (
            <Carousel.Item key={idx}>
              {isImageLoaded && <EditButton uuid={media?.uuid} />}
              {isVideoMedia(media) ? (
                <div
                  className="w-100 h-100 d-flex justify-content-center align-items-center"
                  style={
                    aspectRatio ? { aspectRatio, backgroundColor: 'black' } : {}
                  }
                >
                  <VideoComponent
                    posterUrl={media.custom_thumbnail_url}
                    backgroundUrl={media.thumbnail_url}
                    videoUrl={getVideoUrl(media)}
                    autoplay={false}
                    ref={videoRefs.current[idx]}
                    styles={{ ...commonStyles, height: '100%' }}
                    useProcessedFrame={true}
                    {...instagramPreviewAudio}
                  />
                </div>
              ) : (
                <div
                  className="w-100 h-100"
                  style={
                    aspectRatio
                      ? {
                          aspectRatio,
                          background: `url(${media.image_url}) no-repeat center center`
                        }
                      : {}
                  }
                >
                  <Image
                    className="img-fluid w-100 h-100"
                    style={{
                      objectFit: 'contain',
                      backdropFilter: 'blur(5px)',
                      ...commonStyles
                    }}
                    src={media.image_url}
                  />
                </div>
              )}
            </Carousel.Item>
          ))}
        </Carousel>
      );
    }
  };

  return (
    <div className="w-100 m-2">
      <Card className="w-100 position-relative">
        {/* set the badge to top right */}
        {!knownAspectRatio && false && (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-top">
                {t('lomavisCreator.preview.backgroundWillBeFilledTooltip')}
              </Tooltip>
            }
          >
            <div
              className="badge bg-danger position-absolute end-0"
              style={{ top: -22 }}
            >
              {t('lomavisCreator.preview.backgroundWillBeFilled')}
            </div>
          </OverlayTrigger>
        )}

        {/* Header */}
        <div className="d-flex justify-content-between p-2 px-3">
          <div className="d-flex flex-row align-items-center">
            <div className="w-50px h-50px">
              <OnImagesLoaded
                onLoaded={() => {}}
                placeholder={<PreviewImagesSkeleton />}
              >
                <Image
                  className="img-fluid w-100 h-100"
                  src={platformIcon}
                  roundedCircle
                />
              </OnImagesLoaded>
            </div>
            <div className="d-flex flex-column align-items-start ms-2">
              <span className="fw-bold">{profileName}</span>
              <small className="text-black-50">
                {t('lomavisCreator.previewCards.just_now')}
              </small>
            </div>
          </div>
        </div>

        {/* Media Content */}
        {hasMediaFiles && (
          <div
            className="asset_wrapper w-100 overflow-hidden position-relative"
            style={{
              maxHeight:
                previewMode === PREVIEW_TYPE_COMPACT
                  ? COMPACT_MODE_HEIGHT
                  : '100%'
            }}
          >
            <OnImagesLoaded
              onLoaded={() => {
                if (!isImageLoaded) {
                  setIsImageLoaded(true);
                }
              }}
              placeholder={<PreviewImagesSkeleton />}
            >
              {created_in_lomavis && renderMediaContent()}
              {!created_in_lomavis && (
                <ImportedPostsMediaPreview
                  platform={platform}
                  mediaFiles={mediaFiles}
                />
              )}
            </OnImagesLoaded>
          </div>
        )}

        {/* Instagram Music attribution */}
        {showInstagramMusicControls &&
          previewHasSingleVideo &&
          instagramMusic?.audio_id && (
            <div className="px-2 d-flex align-items-center my-2 gap-2 small text-muted">
              <i className="fa-solid fa-music"></i>
              <span className="text-truncate">
                {instagramMusic.title || instagramMusic.ig_username || '—'}
                {instagramMusic.display_artist
                  ? ` — ${instagramMusic.display_artist}`
                  : ''}
              </span>
            </div>
          )}

        {/* Post Text */}
        {postText && (
          <div className="px-2 d-flex align-items-start my-2">
            <p className="py-2">
              <span
                className="preview-text"
                style={{
                  color: 'black',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word'
                }}
                dangerouslySetInnerHTML={{ __html: postText }}
              />
            </p>
          </div>
        )}

        {/* Icons */}
        <div className="d-flex flex-row justify-content-between px-2 pt-3 border-top align-items-center">
          <div className="d-flex flex-row">
            <p>
              <i className="fa-solid fa-heart"></i>
            </p>
            <p className="ms-3">
              <i className="fa-solid fa-comment"></i>
            </p>
            <p className="ms-3">
              <i className="fa-solid fa-paper-plane"></i>
            </p>
          </div>
          <div>
            <p className="me-2">
              <i className="fa-solid fa-bookmark"></i>
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
